/**
 * src/utils/iso3166-2.ts — Lazy loader for the ISO-3166-2 subdivision dataset.
 *
 * v0.4.9.2.3c22a: replaces hardcoded country/city dropdowns across the
 * three admin pages (Most Wanted, Missing Persons, Stolen Vehicles) and
 * the future RecipientReporterHistory view.
 *
 * The dataset is large (~160 KB raw / ~30 KB gzipped) so it is loaded via
 * a dynamic `import()`. The first call kicks off the fetch; subsequent
 * calls reuse the cached promise + value.
 *
 * Tree-shaking: Vite/Rollup code-splits the dynamic import into a separate
 * chunk that is fetched only when a country dropdown is opened. The
 * /admin/* bundle stays slim.
 */
import type { CountryInfo, Subdivision } from '../data/iso3166-2-data';
import { COUNTRIES, findCountry, type CountryEntry } from '../data/countries';

// Re-export Subdivision + CountryInfo so consumers only need to import this
// utility module rather than the heavy data module.
export type { Subdivision, CountryInfo };

// -------- data load (once per page load) --------

let cachedData: Record<string, CountryInfo> | null = null;
let loadPromise: Promise<Record<string, CountryInfo>> | null = null;

async function loadSubdivisionsData(): Promise<Record<string, CountryInfo>> {
  if (cachedData) return cachedData;
  if (!loadPromise) {
    loadPromise = import('../data/iso3166-2-data').then(m => {
      cachedData = m.default;
      return cachedData!;
    });
  }
  return loadPromise;
}

/**
 * Get the subdivisions (cities / provinces / states) for one country.
 * Returns [] if the country is unknown.
 *
 * First call triggers the dynamic import of the ~160 KB dataset.
 * Subsequent calls return from the module cache.
 */
export async function getSubdivisions(countryAlpha3: string): Promise<Subdivision[]> {
  if (!countryAlpha3) return [];
  const data = await loadSubdivisionsData();
  return data[countryAlpha3]?.subdivisions ?? [];
}

/**
 * Get full country info (alpha-3 + alpha-2 + name + subdivisions).
 */
export async function getCountryInfo(countryAlpha3: string): Promise<CountryInfo | null> {
  if (!countryAlpha3) return null;
  const data = await loadSubdivisionsData();
  return data[countryAlpha3] ?? null;
}

// -------- static helpers (no dynamic import) --------

export { COUNTRIES, findCountry };
export type { CountryEntry };

/**
 * Resolve a subdivision code back to its display name.
 * E.g. "BY-HM" → "Minsk (city)".
 */
export function subdivisionNameLookup(): (code: string) => Promise<string> {
  let cache: Record<string, string> | null = null;
  let loadPromise2: Promise<Record<string, string>> | null = null;

  return async (code: string): Promise<string> => {
    if (!code) return '';
    if (!cache) {
      if (!loadPromise2) {
        loadPromise2 = loadSubdivisionsData().then(data => {
          const map: Record<string, string> = {};
          for (const country of Object.values(data)) {
            for (const sub of country.subdivisions) {
              map[sub.code] = sub.name;
            }
          }
          cache = map;
          return map;
        });
      }
      const map = await loadPromise2;
      return map[code] ?? code;
    }
    return cache[code] ?? code;
  };
}
